import { companyService } from "@/services/companyService";
import { userService } from "@/services/userService";
import {
  ApiErrorResponse,
  ApiResponse,
  COMMON_STATUS,
  CommonParams,
  CompanyDto,
  CompanyParams,
  DEFAULT_ERROR_MESSAGE,
  DEFAULT_SUCCESS_MESSAGE,
  GetCompanyParams,
  GetStoresResp,
  GetUsersResp,
} from "@/types";
import { toastError, toastSuccess } from "@/utils/toastify";
import { create } from "zustand";

export interface CompanyStore {
  companies: ApiResponse<CompanyDto[]> | null;
  company: CompanyDto | null;
  stores: ApiResponse<GetStoresResp[]> | null;
  users: ApiResponse<GetUsersResp[]> | null;
  loading: boolean;
  loadingCompany: boolean;
  error: string | null;
  // Actions
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setCompanies: (companies: ApiResponse<CompanyDto[]> | null) => void;
  setCompany: (company: CompanyDto | null) => void;
  setStores: (stores: ApiResponse<GetStoresResp[]> | null) => void;
  setUsers: (users: ApiResponse<GetUsersResp[]> | null) => void;
  fetchCompanies: (
    params?: GetCompanyParams
  ) => Promise<ApiResponse<CompanyDto[]>>;
  fetchCompanyById: (id: number) => Promise<CompanyDto | null>;
  fetchMyCompany: () => Promise<CompanyDto | null>;
  updateCompany: (id: number, company: CompanyParams) => Promise<CompanyDto>;
  updateCompanyStatus: (
    id: number,
    status: "ENABLE" | "DISABLE"
  ) => Promise<null>;
  updateCompanyTypeToNonMember: (id: number) => Promise<null>;
  createCompany: (company: CompanyParams) => Promise<CompanyDto>;
  deleteCompany: (id: number) => Promise<null>;
  fetchStores: (
    companyId: number,
    params?: CommonParams<{}>
  ) => Promise<ApiResponse<GetStoresResp[]>>;
  fetchMyStores: (
    params?: CommonParams<{}>
  ) => Promise<ApiResponse<GetStoresResp[]>>;
  fetchUsers: (
    companyId: number,
    params?: CommonParams<{}>
  ) => Promise<ApiResponse<GetUsersResp[]>>;
  updateUserStatus: (
    id: number
  ) => Promise<ApiResponse<{ status: "ENABLE" | "DISABLE" }>>;
  // Reset functions
  clearError: () => void;
  clearCompany: () => void;
  resetStore: () => void;
}

export const useCompanyStore = create<CompanyStore>((set, get) => ({
  company: null,
  companies: null,
  stores: null,
  users: null,
  loading: false,
  loadingCompany: false,
  error: null,

  setLoading: (loading: boolean) => set({ loading }),
  setError: (error: string | null) => set({ error }),
  setCompanies: (companies: ApiResponse<CompanyDto[]> | null) =>
    set({ companies }),
  setCompany: (company: CompanyDto | null) => set({ company }),
  setStores: (stores: ApiResponse<GetStoresResp[]> | null) => set({ stores }),
  setUsers: (users: ApiResponse<any[]> | null) => set({ users }),
  fetchCompanies: async (
    params?: GetCompanyParams
  ): Promise<ApiResponse<CompanyDto[]>> => {
    set({ loading: true, error: null });
    try {
      const companies = await companyService.getCompanies(params);
      set({ companies, loading: false });
      return companies;
    } catch (error: any) {
      set({
        error: error instanceof Error ? error.message : DEFAULT_ERROR_MESSAGE,
        loading: false,
      });
      toastError(
        error instanceof Error ? error.message : DEFAULT_ERROR_MESSAGE
      );
      return {
        data: [],
        status: error.status,
        pagination: {
          page: 1,
          pageSize: 14,
          itemCount: 0,
          pageCount: 0,
          hasPreviousPage: false,
          hasNextPage: false,
        },
      };
    }
  },
  fetchCompanyById: async (id: number): Promise<CompanyDto | null> => {
    set({ error: null, loadingCompany: true });
    try {
      const resp = await companyService.getCompanyById(id);
      set({ company: resp.data, loadingCompany: false });
      return resp.data;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : DEFAULT_ERROR_MESSAGE,
        loadingCompany: false,
      });
      toastError(
        error instanceof Error ? error.message : DEFAULT_ERROR_MESSAGE
      );
      return null;
    }
  },
  fetchMyCompany: async (): Promise<CompanyDto | null> => {
    set({ error: null, loadingCompany: true });
    try {
      const resp = await companyService.getMyCompany();
      set({ company: resp.data, loadingCompany: false });
      return resp.data;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : DEFAULT_ERROR_MESSAGE,
        loadingCompany: false,
      });
      toastError(
        error instanceof Error ? error.message : DEFAULT_ERROR_MESSAGE
      );
      return null;
    }
  },
  updateCompany: async (
    id: number,
    params: CompanyParams
  ): Promise<CompanyDto> => {
    set({ error: null });
    let prevDataCompany = get().company;
    let prevDataCompanies = get().companies;
    try {
      // update data in store
      const updatedCompanies: (
        companies: CompanyDto[],
        params: CompanyParams
      ) => CompanyDto[] = (companies, params) =>
        (companies || [])?.map((company) => {
          if (company.id == id) {
            return {
              ...company,
              ...params,
            } as any;
          }
          return company;
        });
      set(({ companies, company }) => {
        return {
          companies: {
            ...(companies || {}),
            data: updatedCompanies(companies?.data || [], params),
          } as ApiResponse<CompanyDto[]>,
          company: {
            ...(company || {}),
            ...params,
          } as CompanyDto,
        };
      });
      // update DB
      const resp = await companyService.updateCompany(id, params);
      toastSuccess(resp.message || DEFAULT_SUCCESS_MESSAGE);
      return resp.data;
    } catch (error: any) {
      set({
        error: error,
        company: prevDataCompany,
        companies: prevDataCompanies,
      });
      // báo lỗi trên input nên không hiện toast
      if (
        ![
          "corporate_code",
          "business_code",
          "business_user.email",
          "email",
          "joining_date",
        ].includes(error.error)
      ) {
        toastError(error.message || DEFAULT_ERROR_MESSAGE);
      }
      throw error;
    }
  },
  updateCompanyStatus: async (
    id: number,
    status: "ENABLE" | "DISABLE"
  ): Promise<null> => {
    set({ error: null });
    const {company, companies} = get();
    try {
      const updatedCompanies: (
        companies: CompanyDto[],
        id: number,
        status: "ENABLE" | "DISABLE"
      ) => CompanyDto[] = (companies, id, status) =>
        (companies || [])?.map((company) => {
          if (company.id == id) {
            return {
              ...company,
              status: status,
            };
          }
          return company;
        });
      set((state) => {
        return {
          company: {
            ...(state.company || {}),
            status: status,
          } as CompanyDto,
          companies: {
            ...state.companies,
            data: updatedCompanies(state.companies?.data || [], id, status),
          } as ApiResponse<CompanyDto[]>,
        };
      });
      const resp = await companyService.updateCompanyStatus(id, status);
      toastSuccess(resp.message || DEFAULT_SUCCESS_MESSAGE);
      return resp.data;
    } catch (error: any) {
      set({
        error: error instanceof Error ? error.message : DEFAULT_ERROR_MESSAGE,
        loading: false,
        company: company,
        companies: companies,
      });
      toastError(error?.message || DEFAULT_ERROR_MESSAGE);
      return null;
    }
  },
  updateCompanyTypeToNonMember: async (id: number): Promise<null> => {
    set({ error: null });
    try {
      const resp = await companyService.updateCompanyTypeToNonMember(id);
      toastSuccess(resp.message || DEFAULT_SUCCESS_MESSAGE);
      return resp.data;
    } catch (error: any) {
      set({ error: error?.message });
      toastError(error?.message || DEFAULT_ERROR_MESSAGE);
      return null;
    }
  },
  createCompany: async (company: CompanyParams): Promise<CompanyDto> => {
    set({ error: null });
    try {
      const resp = await companyService.createCompany(company);
      toastSuccess(resp.message || DEFAULT_SUCCESS_MESSAGE);
      return resp.data;
    } catch (error: any) {
      set({
        error: error,
        loading: false,
      });
      // báo lỗi trên input nên không hiện toast
      if (
        [
          "corporate_code",
          "business_code",
          "business_user.email",
          "email",
          "joining_date",
        ].includes(error.error)
      ) {
        toastError(error.message || DEFAULT_ERROR_MESSAGE);
      }
      throw error as ApiErrorResponse;
    }
  },
  deleteCompany: async (id: number): Promise<null> => {
    set({ error: null });
    try {
      const resp = await companyService.deleteCompany(id);
      toastSuccess(resp.message || DEFAULT_SUCCESS_MESSAGE);
      return resp.data;
    } catch (error: any) {
      set({
        error: error?.message || DEFAULT_ERROR_MESSAGE,
        loading: false,
      });
      toastError(error?.message || DEFAULT_ERROR_MESSAGE);
      return null;
    }
  },
  fetchStores: async (
    companyId: number,
    params?: CommonParams<{}>
  ): Promise<ApiResponse<GetStoresResp[]>> => {
    set({ error: null });
    try {
      const stores = await companyService.getStores(companyId, params);
      set({ stores });
      return stores;
    } catch (error: any) {
      set({
        error: error instanceof Error ? error.message : DEFAULT_ERROR_MESSAGE,
      });
      return {
        data: [],
        status: error.status,
        pagination: {
          page: 1,
          pageSize: 9,
          itemCount: 0,
          pageCount: 0,
          hasPreviousPage: false,
          hasNextPage: false,
        },
      };
    }
  },
  fetchMyStores: async (
    params?: CommonParams<{}>
  ): Promise<ApiResponse<GetStoresResp[]>> => {
    set({ error: null });
    try {
      const stores = await companyService.getMyStores(params);
      set({ stores });
      return stores;
    } catch (error: any) {
      set({
        error: error instanceof Error ? error.message : DEFAULT_ERROR_MESSAGE,
      });
      return {
        data: [],
        status: error.status,
        pagination: {
          page: 1,
          pageSize: 9,
          itemCount: 0,
          pageCount: 0,
          hasPreviousPage: false,
          hasNextPage: false,
        },
      };
    }
  },
  fetchUsers: async (
    companyId: number,
    params?: CommonParams<{}>
  ): Promise<ApiResponse<GetUsersResp[]>> => {
    set({ error: null });
    try {
      const resp = await companyService.getUsers(companyId, params);
      const page = resp.pagination?.page;
      set(({ users }) => ({
        users: {
          ...resp,
          data: [...(page == 1 ? [] : users?.data || []), ...resp.data],
        },
      }));
      return resp;
    } catch (error: any) {
      set({
        error: error instanceof Error ? error.message : DEFAULT_ERROR_MESSAGE,
      });
      return {
        data: [],
        status: error.status,
        pagination: {
          page: 1,
          pageSize: 10,
          itemCount: 0,
          pageCount: 0,
          hasPreviousPage: false,
          hasNextPage: false,
        },
      };
    }
  },

  updateUserStatus: async (id: number) => {
    const currentUsers = get().users;
    try {
      const updatedUsers = {
        ...currentUsers,
        data: currentUsers?.data.map((user) =>
          user.id === id
            ? {
                ...user,
                status:
                  user.status === COMMON_STATUS.ENABLE
                    ? COMMON_STATUS.DISABLE
                    : COMMON_STATUS.ENABLE,
              }
            : user
        ),
      };

      set({
        users: updatedUsers as ApiResponse<GetUsersResp[]>,
        loading: false,
      });
      const response = await userService.updateUserStatus(id);
      toastSuccess(response.message || DEFAULT_SUCCESS_MESSAGE);

      return response;
    } catch (error: any) {
      const errorMsg = error.message || DEFAULT_ERROR_MESSAGE;
      set({
        error: errorMsg,
        loading: false,
        users: currentUsers,

      });
      toastError(errorMsg);
      throw error;
    }
  },

  clearError: () => set({ error: null }),
  clearCompany: () => set({ company: null }),
  resetStore: () =>
    set({
      companies: null,
      company: null,
      loading: false,
      error: null,
    }),
}));
